"use client";

import { createContext, useContext, type AnchorHTMLAttributes, type ReactNode } from "react";

/**
 * Routing is **injected**, never imported (`02-architecture.md`,
 * `13-house-style.md` § 5.4).
 *
 * The Capacitor POS consumes this package but not `@tovi/app-core`, so a single
 * `next/link` import here would break the till's build. The apps supply their
 * own navigation component through this context: Next's `Link` in the web apps,
 * TanStack Router's in the till.
 */
export type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
  children: ReactNode;
};

export type LinkComponent = (props: LinkProps) => ReactNode;

const DefaultLink: LinkComponent = ({ href, children, ...rest }) => (
  <a href={href} {...rest}>
    {children}
  </a>
);

const LinkContext = createContext<LinkComponent>(DefaultLink);

export function LinkProvider({
  component,
  children,
}: {
  component: LinkComponent;
  children: ReactNode;
}) {
  return <LinkContext.Provider value={component}>{children}</LinkContext.Provider>;
}

export function useLinkComponent(): LinkComponent {
  return useContext(LinkContext);
}

export function Link(props: LinkProps) {
  const Component = useLinkComponent();
  // The component is read from context, not built here. The rule cannot see
  // through the provider to know the value is stable; `app-core`'s `NextLink`
  // is declared at module scope, so this identity never changes and nothing
  // below it remounts. The rule stays on for the rest of the repo: an inline
  // arrow passed to `LinkProvider` would remount every screen under it, and
  // that is the call site worth guarding.
  // oxlint-disable-next-line react/static-components
  return <Component {...props} />;
}
